import { useState, useEffect, useMemo } from "react";
import { useCategoriesStore, useItemsStore, useUIStore } from "../store";
import { Category } from "../interfaces/domain/menu";
import { Swiper as SwiperType } from "swiper";
import { FiHome } from "react-icons/fi";

export const HOME_ITEM = {
  id: "home-link",
  name: "",
  icon: FiHome,
  priority: -1,
  type: "home" as const,
  data: null,
};

const readCssPxNumber = (varName: string, fallback: number) => {
  if (typeof window === "undefined") return fallback;
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? n : fallback;
};

export const useCarrouselCategories = () => {
  const allCategories = useItemsStore((s) => s.menuData.categories);
  const theme = useUIStore((s) => s.theme);
  const cfgVersion = useUIStore((s) => s.config.version);
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
  const activeCategoryId = useCategoriesStore((s) => s.categorySelected?.id);
  const [gap, setGap] = useState(12);

  const sortedItems = useMemo(() => {
    return [...(allCategories || [])]
      .sort((a, b) => (a.position ?? 999) - (b.position ?? 999))
      .flatMap((cat: Category) => [
        { id: cat.id, name: cat.name, priority: cat.position ?? 999, data: cat },
        ...[...(cat.subcategories ?? [])]
          .sort((a, b) => (a.position ?? 999) - (b.position ?? 999))
          .map((sub) => ({ id: sub.id, name: sub.name, priority: sub.position ?? 999, data: sub })),
      ]);
  }, [allCategories]);

  useEffect(() => {
    setGap(readCssPxNumber("--carousel-categories-gap", 12));
  }, [theme, cfgVersion]);

  useEffect(() => {
    swiperInstance?.update();
  }, [sortedItems, gap, swiperInstance]);

  // Sincronizar carrusel con categoría activa al hacer scroll
  useEffect(() => {
    if (!activeCategoryId || !swiperInstance) return;
    const index = sortedItems.findIndex((i) => i.id === activeCategoryId);
    if (index !== -1) {
      swiperInstance.slideTo(index);
    }
  }, [activeCategoryId, swiperInstance, sortedItems]);

  return {
    sortedItems,
    gap,
    setSwiperInstance,
    shouldRender: sortedItems.length > 1,
  };
};
